import {Box, Checkbox, ExpandedIndex, Grid, IconButton, Input, Select, Stack, Text, Tooltip} from "@chakra-ui/react";
import {KeyboardEvent, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {BoundariesZone as BoundariesZoneType, SpotInfo, Zone} from "../../../components/types";
import {AppContext, AppContextI} from "../../../components/context/ContextWrapper";
import AreaZone, {SoundFileDef} from "./AreaZone";
import AmbientZoneRules from "./AmbientZoneRules";
import {BiTrash, BiErrorCircle} from "react-icons/bi";
import {useConfirmDialog} from "../../../components/common/ConfirmDialogProvider";
import {findReferencesToZone, hasAnyDanglingRule} from "../../../components/rules/ruleReferences";
import {buildDeleteZoneConfirmBody} from "../../../components/rules/deleteConfirmBody";
import {isZoneIosCompatible} from "../../../components/area/iosCompatibility";
import {zoneHasWavSoundFile} from "../../../components/soundfiles/wavWarning";
import HelpTooltip from "../../../components/help/HelpTooltip";
import {ZoneContext, ZoneContextI} from "../../../components/context/ZoneContextWrapper";
import BoundariesZone from "./BoundariesZone";

type Props = {
    zones?: Zone[]
}

type AmbientListItem =
    | {id: string; type: "boundaries"; label: string; zone: BoundariesZoneType}
    | {id: string; type: "ambient"; label: string; zone: Zone};

type ZoneCardProps = {
    zone: Zone
    allSpotsInfo: SpotInfo[]
    initialExpandedIndex?: ExpandedIndex
    onExpandedIndexChange?: (index: ExpandedIndex) => void
}

export const AmbientZoneCard = ({zone, allSpotsInfo, initialExpandedIndex, onExpandedIndexChange}: ZoneCardProps) => {
	const appContext: AppContextI = useContext<AppContextI>(AppContext);
	const bleEnabled = appContext.isBleEnabled();
	const {confirm} = useConfirmDialog();
	const updateZoneRef = useRef(appContext.updateZone);
	updateZoneRef.current = appContext.updateZone;
	const isDraft = appContext.isDraft();
	const [labelInput, setLabelInput] = useState<string>(zone.label || zone.name || "");
	const [beaconSensitivity, setBeaconSensitivity] = useState<number>(zone.beaconSensitivity ?? 8);
	const [stopOtherAmbients, setStopOtherAmbients] = useState<boolean>(zone.stopOtherAmbients ?? false);
	const [loop, setLoop] = useState<boolean>(zone.loop ?? false);
	const [runOnes, setRunOnes] = useState<boolean>(zone.runOnes ?? false);

    useEffect(() => {
        setLabelInput(zone.label || zone.name || "");
    }, [zone.id, zone.label, zone.name]);

    useEffect(() => {
        setBeaconSensitivity(zone.beaconSensitivity ?? 8);
    }, [zone.id, zone.beaconSensitivity]);

	useEffect(() => {
		if (!isDraft) {
			return;
		}
		const currentSensitivity = zone.beaconSensitivity ?? 8;
		if (beaconSensitivity !== currentSensitivity) {
			updateZoneRef.current(zone, {
				...zone,
				beaconSensitivity: beaconSensitivity
			} as Zone);
		}
	}, [beaconSensitivity, isDraft, zone]);

    useEffect(() => {
        setStopOtherAmbients(zone.stopOtherAmbients ?? false);
    }, [zone.id, zone.stopOtherAmbients]);

	useEffect(() => {
		if (!isDraft) {
			return;
		}
		const currentValue = zone.stopOtherAmbients ?? false;
		if (stopOtherAmbients !== currentValue) {
			updateZoneRef.current(zone, {
				...zone,
				stopOtherAmbients: stopOtherAmbients
			} as Zone);
		}
	}, [isDraft, stopOtherAmbients, zone]);

    useEffect(() => {
        setLoop(zone.loop ?? false);
    }, [zone.id, zone.loop]);

	useEffect(() => {
		if (!isDraft) {
			return;
		}
		const currentValue = zone.loop ?? false;
		if (loop !== currentValue) {
			updateZoneRef.current(zone, {
				...zone,
				loop: loop
			} as Zone);
		}
	}, [isDraft, loop, zone]);

    useEffect(() => {
        setRunOnes(zone.runOnes ?? false);
    }, [zone.id, zone.runOnes]);

	useEffect(() => {
		if (!isDraft) {
			return;
		}
		const currentValue = zone.runOnes ?? false;
		if (runOnes !== currentValue) {
			updateZoneRef.current(zone, {
				...zone,
				runOnes: runOnes
			} as Zone);
		}
	}, [isDraft, runOnes, zone]);

    const handleNameBlur = () => {
        const nextName = labelInput.trim();
        if (nextName === (zone.label || zone.name) || !appContext.isDraft()) {
            setLabelInput(zone.label || zone.name || "");
            return;
        }
        appContext.updateZone(zone, {
            ...zone,
            name: nextName || zone.name || "Ambient Zone",
            label: nextName || zone.label || zone.name || "Ambient Zone"
        } as Zone);
    };

    const soundFiles: SoundFileDef[] = [
        {
            name: "ambient",
            title: "Ambient Sound",
            showFadeControls: true
        }
    ];

    const allSpotsForScan = appContext.getSpots() || [];
    const ambientZonesForScan = appContext.getArea()?.zones?.ambientZones || [];
    const hasDangling = hasAnyDanglingRule(zone.triggerRules?.rules, allSpotsForScan, ambientZonesForScan);
    const macOnly = !isZoneIosCompatible(zone);
    const wavInStream = appContext.getArea()?.audioMode === "stream" && zoneHasWavSoundFile(zone);

    const headerContent = (
        <Stack spacing={3}>
            <Grid templateColumns={"1fr min-content min-content"} gap={2} alignItems={"center"}>
                <Input
                    size={"sm"}
                    value={labelInput}
                    placeholder={"Ambient zone name"}
                    onChange={(event) => setLabelInput(event.target.value)}
                    onBlur={handleNameBlur}
                    onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                        if (event.key === "Enter") {
                            event.currentTarget.blur();
                        }
                    }}
                    disabled={!appContext.isDraft()}
                />
                <Box display={"flex"} alignItems={"center"} gap={"6px"}>
                    {hasDangling ? (
                        <Tooltip label={"This ambient zone has rules referencing missing spots or ambient zones"}>
                            <Box as={"span"} color={"red.500"} display={"inline-flex"} alignItems={"center"} fontSize={"lg"}>
                                <BiErrorCircle/>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {macOnly ? (
                        <Tooltip label={"This ambient zone is only triggered by MAC-based beacons (not iOS-compatible). Add an iBeacon, WiFi, or GPS trigger."}>
                            <Box as={"span"} color={"orange.500"} display={"inline-flex"} alignItems={"center"} fontSize={"lg"}>
                                <BiErrorCircle/>
                            </Box>
                        </Tooltip>
                    ) : null}
                    {wavInStream ? (
                        <Tooltip label={"This ambient zone uses a WAV file. WAV is a poor fit for streaming — high data usage, slow buffering, more underruns. Re-encode to MP3 or AAC."}>
                            <Box as={"span"} color={"orange.500"} display={"inline-flex"} alignItems={"center"} fontSize={"lg"}>
                                <BiErrorCircle/>
                            </Box>
                        </Tooltip>
                    ) : null}
                </Box>
                <IconButton
                    aria-label={"Delete ambient zone"}
                    icon={<BiTrash/>}
                    variant={"ghost"}
                    onClick={async () => {
                        if (!appContext.isDraft()) {
                            return;
                        }
                        const area = appContext.getArea();
                        if (!area || !Array.isArray(area.zones?.ambientZones)) {
                            return;
                        }
                        const ambientZones = area.zones.ambientZones;
                        const allSpots = appContext.getSpots() || [];
                        const scanResult = findReferencesToZone(zone.id, allSpots, ambientZones);
                        const approved = await confirm({
                            title: "Delete ambient zone",
                            body: buildDeleteZoneConfirmBody(scanResult),
                            confirmText: "Delete",
                            isDestructive: true
                        });
                        if (!approved) {
                            return;
                        }
                        const nextZones = ambientZones.filter((item) => item?.id !== zone.id);
                        appContext.updateArea(area, {
                            ...area,
                            zones: {
                                ...area.zones,
                                ambientZones: nextZones
                            }
                        });
                    }}
                    isDisabled={!appContext.isDraft()}
                />
            </Grid>
            <Checkbox
                isChecked={stopOtherAmbients}
                onChange={(event) => setStopOtherAmbients(event.target.checked)}
                isDisabled={!appContext.isDraft()}
                paddingLeft={4}
            >
                Stop all ambients
            </Checkbox>
        </Stack>
    );

    return (
        <AreaZone
            zone={zone}
            description={""}
            soundFiles={soundFiles}
            allowGpsTriggers={true}
            rulesContent={<AmbientZoneRules zone={zone} allSpotsInfo={allSpotsInfo}/>}
            headerContent={headerContent}
            soundFilesDisabled={stopOtherAmbients}
            initialExpandedIndex={initialExpandedIndex}
            onExpandedIndexChange={onExpandedIndexChange}
        >
            <Stack spacing={2}>
                <HelpTooltip content={"When on, the ambient sound loops indefinitely until another ambient or spot pauses or stops it. When off, the sound plays through once and stops."}>
                    <Checkbox
                        isChecked={loop}
                        onChange={(event) => setLoop(event.target.checked)}
                        isDisabled={!appContext.isDraft() || stopOtherAmbients}
                    >
                        Loop the ambient sound
                    </Checkbox>
                </HelpTooltip>
                <HelpTooltip content={"When on, the ambient plays at most once per session — after it finishes naturally it won't play again. Interrupted playback can still resume on a later trigger. Ignored when Loop is on."}>
                    <Checkbox
                        isChecked={runOnes}
                        onChange={(event) => setRunOnes(event.target.checked)}
                        isDisabled={!appContext.isDraft() || stopOtherAmbients || loop}
                    >
                        Play only once per session
                    </Checkbox>
                </HelpTooltip>
            </Stack>
            {bleEnabled ? (
                <Box>
                    <HelpTooltip content={"Sets how strong a beacon signal must be before this zone triggers. Higher values are more sensitive and react from farther away; lower values keep the trigger tighter."}>
                        <Text fontWeight={"semibold"}>Beacon sensitivity:</Text>
                    </HelpTooltip>
                    <Select
                        name={"BeaconSensitivity"}
                        value={beaconSensitivity}
                        onChange={(event) => setBeaconSensitivity(+event.target.value)}
                        size={"sm"}
                        disabled={!appContext.isDraft()}
                    >
                        <option value={1}>1 (-50) - least sensitive</option>
                        <option value={2}>2 (-55)</option>
                        <option value={3}>3 (-60)</option>
                        <option value={4}>4 (-65)</option>
                        <option value={5}>5 (-70)</option>
                        <option value={6}>6 (-75)</option>
                        <option value={7}>7 (-80)</option>
                        <option value={8}>8 (-85) - default</option>
                        <option value={9}>9 (-90)</option>
                        <option value={10}>10 (-95) - most sensitive</option>
                    </Select>
                </Box>
            ) : null}
        </AreaZone>
    );
};

const AmbientZones = ({zones}: Props) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const zoneContext: ZoneContextI = useContext<ZoneContextI>(ZoneContext);
    const area = appContext.getArea();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const ambientZones = useMemo(() => zones || [], [zones]);
    const sortedZones = useMemo(() => {
        return [...ambientZones].sort((a, b) => (a.label || a.name || "").localeCompare(b.label || b.name || "", undefined, {numeric: true, sensitivity: "base"}));
    }, [ambientZones]);
    const listItems = useMemo<AmbientListItem[]>(() => {
        const items: AmbientListItem[] = [];
        if (area?.zones?.boundaries) {
            items.push({
                id: area.zones.boundaries.id,
                type: "boundaries",
                label: area.zones.boundaries.label || area.zones.boundaries.name || "Boundaries",
                zone: area.zones.boundaries
            });
        }
        sortedZones.forEach((zone) => {
            items.push({
                id: zone.id,
                type: "ambient",
                label: zone.label || zone.name || "Ambient Zone",
                zone: zone
            });
        });
        return items;
    }, [area?.zones?.boundaries, sortedZones]);
    const [selectedZoneId, setSelectedZoneId] = useState<string | undefined>(undefined);
    const [zoneAccordionSections, setZoneAccordionSections] = useState<ExpandedIndex>([0]);
    const [bottomHeightPx, setBottomHeightPx] = useState<number>(535);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [originalUserSelect, setOriginalUserSelect] = useState<string | undefined>(undefined);
    const listItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const selectedZone = zoneContext.getSelectedZone();
    const dividerHeightPx = 6;
    const minBottomPx = 200;
    const minTopPx = 50;

    const spots = appContext.getSpots();
    const allSpotsInfo = useMemo(() => {
        return (spots || []).map((spot) => ({
            id: spot.id,
            key: spot.key,
            name: spot.name,
            type: spot.type,
            modifiedAt: spot.modifiedAt
        }));
    }, [spots]);

    useEffect(() => {
        if (listItems.length === 0) {
            setSelectedZoneId(undefined);
            return;
        }
        if (selectedZone && listItems.some((item) => item.id === selectedZone)) {
            setSelectedZoneId(selectedZone);
            return;
        }
        if (!selectedZoneId || !listItems.some((item) => item.id === selectedZoneId)) {
            setSelectedZoneId(listItems[0].id);
        }
    }, [listItems, selectedZone, selectedZoneId]);

    useEffect(() => {
        if (!selectedZone || !listItems.some((item) => item.id === selectedZone)) {
            return;
        }
        setSelectedZoneId(selectedZone);
        const element = listItemRefs.current.get(selectedZone);
        element?.scrollIntoView({block: "nearest"});
    }, [listItems, selectedZone]);

    const selectedItem = listItems.find((item) => item.id === selectedZoneId);

    const recomputeSplit = () => {
        if (!containerRef.current) {
            return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        const usableHeight = rect.height - dividerHeightPx;
        if (usableHeight <= 0) {
            return;
        }

        const maxBottom = Math.max(minBottomPx, usableHeight - minTopPx);
        const clampedBottom = Math.max(minBottomPx, Math.min(maxBottom, bottomHeightPx));
        setBottomHeightPx(clampedBottom);
    };

    useLayoutEffect(() => {
        recomputeSplit();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem?.id, listItems.length, bottomHeightPx]);

    useEffect(() => {
        const onResize = () => recomputeSplit();
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem?.id, listItems.length, bottomHeightPx]);

    useEffect(() => {
        if (!isDragging) {
            return;
        }
        const onMouseMove = (event: MouseEvent) => {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const usableHeight = rect.height - dividerHeightPx;
            const offsetY = event.clientY - rect.top;
            const proposedBottom = usableHeight - offsetY;
            const maxBottom = Math.max(minBottomPx, usableHeight - minTopPx);
            const clampedBottom = Math.max(minBottomPx, Math.min(maxBottom, proposedBottom));
            setBottomHeightPx(clampedBottom);
        };
        const onMouseUp = () => {
            setIsDragging(false);
            if (originalUserSelect !== undefined) {
                document.body.style.userSelect = originalUserSelect;
                setOriginalUserSelect(undefined);
            }
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [isDragging, originalUserSelect]);

    const rowsTemplate = `1fr ${dividerHeightPx}px ${bottomHeightPx}px`;

    return (
        <Box height={"100%"} display={"flex"} flexDirection={"column"} minHeight={0}>
            <Text fontWeight={"semibold"} marginBottom={3}>Ambient zones</Text>
            <Text fontSize={"sm"} color={"gray.600"} marginBottom={3}>
                Ambient zones play a single ambient sound when triggered.
            </Text>
            {listItems.length === 0 ? (
                <Text fontSize={"sm"} color={"gray.600"}>No ambient zones yet.</Text>
            ) : (
                <Grid templateRows={rowsTemplate} flex={"1"} minHeight={0} ref={containerRef}>
                    <Box overflowY={"auto"} minHeight={0}>
                        {listItems.map((item) => {
                            const isSelected = item.id === selectedZoneId;
                            return (
                                <Box
                                    key={item.id}
                                    ref={(element) => {
                                        if (element) {
                                            listItemRefs.current.set(item.id, element);
                                        } else {
                                            listItemRefs.current.delete(item.id);
                                        }
                                    }}
                                    paddingY={"6px"}
                                    paddingX={"4px"}
                                    cursor={"pointer"}
                                    onClick={() => {
                                        if (document.activeElement instanceof HTMLElement) {
                                            document.activeElement.blur();
                                        }
                                        setSelectedZoneId(item.id);
                                        zoneContext.setSelectedZone(item.id);
                                    }}
                                >
                                    <Grid templateColumns={"max-content 1fr"} alignItems={"center"} gap={2}>
                                        <Box color={isSelected ? "#d97706" : undefined} fontWeight={isSelected ? "semibold" : "normal"}>
                                            {item.label}
                                        </Box>
                                        <Box flexGrow={1} borderBottom={"1px solid #e2e8f0"}/>
                                    </Grid>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box
                        height={"6px"}
                        backgroundColor={"#e2e8f0"}
                        cursor={"row-resize"}
                        onMouseDown={() => {
                            setIsDragging(true);
                            if (originalUserSelect === undefined) {
                                setOriginalUserSelect(document.body.style.userSelect);
                            }
                            document.body.style.userSelect = "none";
                        }}
                    />
                    <Box overflowY={"auto"} minHeight={0}>
                        {selectedItem ? (
                            selectedItem.type === "boundaries" ? (
                                <BoundariesZone
                                    key={`boundaries-${selectedItem.id}`}
                                    active={true}
                                    zone={selectedItem.zone}
                                    initialExpandedIndex={zoneAccordionSections}
                                    onExpandedIndexChange={setZoneAccordionSections}
                                />
                            ) : (
                                <AmbientZoneCard
                                    key={`ambient-${selectedItem.id}`}
                                    zone={selectedItem.zone}
                                    allSpotsInfo={allSpotsInfo}
                                    initialExpandedIndex={zoneAccordionSections}
                                    onExpandedIndexChange={setZoneAccordionSections}
                                />
                            )
                        ) : (
                            <Box padding={4}>
                                <Text>Select an ambient zone to view details</Text>
                            </Box>
                        )}
                    </Box>
                </Grid>
            )}
        </Box>
    );
};

export default AmbientZones;
